import { Skeleton, Typography } from '@neo4j-ndl/react';
import { MagnifyingGlassIconOutline } from '@neo4j-ndl/react/icons';
import { type ProjectSummary } from '@nx/state';
import { isNonEmptyString, isNotNullish } from '@nx/stdlib';
import { Grid } from '@nx/ui';
import { useCallback, useMemo, useRef, useState } from 'react';

import { NewProjectCard, ProjectCard } from './project-card';

type Props = {
  projects: ProjectSummary[];
  filter: string;
};

export const ProjectList = ({ projects, filter }: Props) => {
  const [visibleItems, setVisibleItems] = useState(10);
  const observer = useRef<IntersectionObserver>();

  const normalizedFilter = filter.trim().toLocaleLowerCase();
  const filteredProjects = useMemo(() => {
    if (isNonEmptyString(normalizedFilter)) {
      return projects.filter(({ name, id }) =>
        [name, id].some((str) => str.toLocaleLowerCase().includes(normalizedFilter)),
      );
    }
    return projects;
  }, [normalizedFilter, projects]);

  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (isNotNullish(entry) && entry.isIntersecting && filteredProjects.length > visibleItems) {
          setVisibleItems(visibleItems + 5);
        }
      });

      if (isNotNullish(node)) {
        observer.current.observe(node);
      }
    },
    [visibleItems, filteredProjects],
  );

  if (!filteredProjects.length) {
    return (
      <div className="flex h-[218px] items-center justify-center">
        <h6>No projects found</h6>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4">
      <div className="grid grid-cols-[repeat(auto-fit,_minmax(49%,_1fr))] gap-3">
        {filteredProjects.toSpliced(visibleItems).map((project) => (
          <ProjectCard key={project.id} projectSummary={project} ref={lastElementRef} />
        ))}
      </div>
    </div>
  );
};

type NewProjectListProps = {
  projects: ProjectSummary[];
  filter: string;
  isLoading: boolean;
};

const newProjectCardCols = { min: 8, sm: 4, md: 6 };

export const NewProjectList = ({ projects, filter, isLoading }: NewProjectListProps) => {
  const normalizedFilter = filter.trim().toLocaleLowerCase();
  const filteredProjects = useMemo(() => {
    if (isNonEmptyString(normalizedFilter)) {
      return projects.filter(({ name, id }) =>
        [name, id].some((str) => str.toLocaleLowerCase().includes(normalizedFilter)),
      );
    }
    return projects;
  }, [normalizedFilter, projects]);

  if (isLoading) {
    return (
      <Grid>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={index}
            as={Grid.Item}
            className="rounded-1xl h-36 w-full"
            htmlAttributes={{
              cols: newProjectCardCols,
            }}
          />
        ))}
      </Grid>
    );
  }

  return (
    <Grid>
      {filteredProjects.length === 0 && (
        <Grid.Item
          cols={{ min: 12 }}
          className="border-palette-neutral-border-weak rounded-1xl bg-palette-neutral-bg-weak flex h-36 w-full flex-col items-center justify-center gap-1 border p-6"
        >
          <MagnifyingGlassIconOutline className="text-palette-neutral-icon size-12" />
          <Typography variant="body-medium">No results found</Typography>
        </Grid.Item>
      )}
      {filteredProjects.length > 0 &&
        filteredProjects.map((project) => (
          <Grid.Item key={project.id} cols={newProjectCardCols}>
            <NewProjectCard projectSummary={project} />
          </Grid.Item>
        ))}
    </Grid>
  );
};
